// Gemeinsames Mietkonto (README §6) – Typen.

export type Quelle = 'bankabgleich' | 'mietkonto' | 'mahnwesen' | 'mieteingang';

export const QUELLE_LABEL: Record<Quelle, string> = {
  bankabgleich: 'Bankabgleich',
  mietkonto: 'Mietkonto',
  mahnwesen: 'Mahnwesen',
  mieteingang: 'Mieteingang',
};

/**
 * Konto-Schlüssel: je Mietverhältnis ein Konto (Entscheidung 12.10.2026).
 * MV/SEV: „mieter:<mieter.id>“ · WEG: „eigentuemer:<eigentuemer.id>:<einheit.id>“.
 * Bleibt auch erhalten, wenn ein ehemaliger Mieter endgültig gelöscht wird.
 */
export type KontoSchluessel = string;

export function mieterKonto(mieterId: string): KontoSchluessel { return `mieter:${mieterId}`; }
export function eigentuemerKonto(eigentuemerId: string, einheitId: string): KontoSchluessel { return `eigentuemer:${eigentuemerId}:${einheitId}`; }

/** Eine Zahlung (Zeile aus mietkonto_buchungen). */
export interface Buchung {
  id: string;
  kontoSchluessel: KontoSchluessel;
  einheitId: string;
  /** YYYY-MM-DD */
  datum: string;
  betrag: number;
  quelle: Quelle;
  zahler: string;
  storniert: boolean;
}

export type SollArt = 'miete' | 'hausgeld' | 'anfangssaldo';

/** Eine Forderung (Soll) – entsteht aus Mietverhältnis bzw. Hausgeld. */
export interface SollPosten {
  /** Monat, dem das Soll zugeordnet ist (YYYY-MM-01) */
  monat: string;
  /** Fälligkeitstag (YYYY-MM-DD) – Reihenfolge der Verrechnung, Beginn des Verzugs */
  faellig: string;
  betrag: number;
  art: SollArt;
}

export interface MonatsZeile {
  /** YYYY-MM-01 */
  monat: string;
  /** „Okt 26“ */
  label: string;
  soll: number;
  /** verrechnete Zahlungen (höchstens bis zum Soll, Überschuss → Guthaben) */
  ist: number;
}

/**
 * Konto eines Mietverhältnisses (README §6 LedgerEntry, erweitert):
 * key = Konto-Schlüssel, einheitId = zugehörige Einheit, aktiv = Mieter wohnt noch / Eigentum läuft.
 */
export interface LedgerEntry {
  key: KontoSchluessel;
  einheitId: string;
  /** Mieter- bzw. Eigentümer-ID (für Mahnung, Kontakt) */
  personId: string | null;
  aktiv: boolean;
  bez: string;
  /** MV: Mieter · WEG: Eigentümer */
  payer: string;
  /** MV: aktuelle Gesamtmiete (0 nach Auszug) · WEG: Hausgeld (inkl. Rücklage) */
  monthly: number;
  /** letzte 12 Monate, ältester zuerst */
  months: MonatsZeile[];
  pays: { id: string; d: string; b: number; src: Quelle }[];
  /** Summen seit Beginn des Mietkontos */
  sollSum: number;
  istSum: number;
  /** ist − soll; negativ = Rückstand */
  saldo: number;
  /** max(0, −saldo) */
  offen: number;
  /** offener Betrag im laufenden Monat */
  oktOpen: number;
  /** Fälligkeit des ältesten nicht vollständig bezahlten Postens (Verzugsbeginn) */
  offenSeit: string | null;
}

export interface Mietkonto {
  isWeg: boolean;
  /** alle Konten (aktuelle und ehemalige Mietverhältnisse) */
  list: LedgerEntry[];
  /** nach Konto-Schlüssel */
  map: Record<KontoSchluessel, LedgerEntry>;
  /** Σ offen über alle Konten (inkl. ehemaliger Mieter) */
  offen: number;
}
